import listingSource from './listing.json';
import { checkGameListing, controllerSpecFromListing } from '@forgesworn/joystick-protocol';
/**
 * Tank Royale host entry.
 *
 * Boots the joystick host SDK in multi-player mode, generates the
 * pairing QR (one shared URL — phones pick the next available slot),
 * runs the world step + render at 60Hz, and routes phone inputs back
 * into the world.
 */

import './style.css';
import '@forgesworn/joystick-pairing-ui/style.css';
import { mountGameControls } from '@forgesworn/joystick-pairing-ui';
import QRCode from 'qrcode';
import {
  startControllerHost,
  type ControllerHost,
  type ControllerSpec,
} from '@forgesworn/joystick-host';
import {
  autoFillAI, applyInput, newWorld, removeTank, spawnTank, step, triggerShake,
  PLAYER_COLOURS, MAX_PLAYERS, type World, type Tank, type Bullet,
} from './game.js';
import {
  acceptName, buildSpectatorView as buildSpectatorViewData, pauseMessage,
  playerSpec, roundWinLabel, spectatorSpec as spectatorSpecFor,
} from './controller-strings.js';
import { draw, type RenderFrame, type CheerBurst } from './render.js';
import {
  setAudioPaused, initAudio, isMuted, sfxHit, sfxKill, sfxLose, sfxShield, sfxShoot, sfxWin,
  startHeartbeat, stopHeartbeat, toggleMuted,
} from './audio.js';

// Controls come from the game's published listing (src/listing.json), so the
// catalogue entry and the controller the phone draws cannot drift apart.
const listingCheck = checkGameListing(listingSource);
if (!listingCheck.ok) throw new Error(`Invalid game listing: ${listingCheck.errors.join('; ')}`);
const LISTING_SPEC = controllerSpecFromListing(listingCheck.listing)!;
const PAD_URL = import.meta.env.VITE_PAD_URL ?? 'https://pad.forgesworn.dev';
const BROKER = import.meta.env.VITE_CONTROLLER_WS ?? 'wss://controller.pallasite.app/';

const canvas = document.getElementById('game');
if (!(canvas instanceof HTMLCanvasElement)) throw new Error('canvas missing');
const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('2d context missing');

const world: World = newWorld();
const joinState = new Map<number, 'open' | 'taken'>();
for (let i = 0; i < MAX_PLAYERS; i++) joinState.set(i, 'open');

let qrDataUrl: string | null = null;

const host: ControllerHost = startControllerHost({
  ws: BROKER,
  multi: true,
  pwaUrl: PAD_URL,
});

// Boot splash + reconnect overlay — hook into the host SDK's status
// stream. Once the first 'waiting' or 'paired' fires, fade the boot
// out. If we later see 'reconnecting' / 'closed', surface the
// reconnect card; hide it again on recovery.
let bootHidden = false;
host.onStatus((s) => {
  const bootEl = document.getElementById('boot');
  const reconnectEl = document.getElementById('reconnect');
  if (s.kind === 'waiting' || s.kind === 'paired') {
    if (!bootHidden && bootEl) {
      bootEl.classList.add('hidden');
      setTimeout(() => bootEl.remove(), 600);
      bootHidden = true;
    }
    if (reconnectEl) {
      reconnectEl.classList.remove('show');
      reconnectEl.classList.add('hidden');
    }
  } else if (s.kind === 'reconnecting' || s.kind === 'closed') {
    if (bootHidden && reconnectEl) {
      reconnectEl.classList.add('show');
      reconnectEl.classList.remove('hidden');
    }
  }
});

// Generate the QR for the pairing URL up front. The same URL is used
// by every player — the broker assigns each one a fresh slot.
void QRCode.toDataURL(host.pairingUrl, {
  margin: 2,
  width: 1000,
  color: { dark: '#0a0a12', light: '#cfd8ff' },
}).then((url) => { qrDataUrl = url; });

// Spectator slots — phones the broker handed us past MAX_PLAYERS. We
// don't spawn tanks for them; instead they get a tiny CHEER controller
// and periodic spectator-view leaderboard frames.
const spectatorSlots = new Set<number>();
const cheers: CheerBurst[] = [];

function spectatorSpec(slot: number): ControllerSpec {
  return spectatorSpecFor(slot, PLAYER_COLOURS[slot % PLAYER_COLOURS.length] ?? '#b48cff');
}
function buildSpectatorView(_slot: number): ReturnType<typeof buildSpectatorViewData> {
  return buildSpectatorViewData({
    tanks: [...world.tanks.values()],
    roundKind: world.round.kind,
    winnerP: world.round.kind === 'over' ? world.round.winnerP : null,
    names: playerNames,
    tournamentTotals,
    lastCheerSlot: cheers.length > 0 ? cheers[cheers.length - 1]!.slot : null,
  });
}

const readyPlayer: Parameters<ControllerHost['onPlayerJoin']>[0] = (player) => {
  // Past the player cap → treat as spectator. Send a CHEER-only spec
  // and queue an immediate leaderboard frame.
  if (player.p >= MAX_PLAYERS) {
    spectatorSlots.add(player.p);
    host.sendToPlayer(player.p, { type: 'controller-spec', spec: spectatorSpec(player.p) });
    host.sendSpectatorView(player.p, buildSpectatorView(player.p));
    return;
  }
  const colour = PLAYER_COLOURS[player.p % PLAYER_COLOURS.length]!;
  // Kick whatever was in this slot (bot if mid-game replacement,
  // nothing if cold lobby) and place a fresh human tank.
  if (!world.tanks.has(player.p) || world.tanks.get(player.p)!.ai) {
    removeTank(world, player.p);
    spawnTank(world, player.p, /* ai */ false);
  }
  joinState.set(player.p, 'taken');
  // Ship an addressed coloured spec so the phone shows its team colour.
  const spec: ControllerSpec = playerSpec(LISTING_SPEC, player.p, colour);
  host.sendToPlayer(player.p, { type: 'controller-spec', spec });
  // A resumed player retains their tank, score and round state.
};
host.onPlayerJoin(readyPlayer);
host.onPlayerResume(readyPlayer);
host.onPlayerSuspend((player) => {
  const tank = world.tanks.get(player.p);
  if (tank) tank.thrust = false;
});

// Phone announces its running tournament total + chosen name on pair-
// up; the game surface displays both as per-player badges.
const tournamentTotals = new Map<number, number>();
const playerNames = new Map<number, string>();
host.onPhoneContext((p, ctx) => {
  if (ctx) {
    tournamentTotals.set(p, ctx.tournamentTotal);
    const name = acceptName(ctx.name);
    if (name) playerNames.set(p, name);
    else playerNames.delete(p);
  } else {
    tournamentTotals.delete(p);
    playerNames.delete(p);
  }
});

host.onPlayerLeave((player) => {
  if (spectatorSlots.delete(player.p)) {
    tournamentTotals.delete(player.p);
    return;
  }
  removeTank(world, player.p);
  joinState.set(player.p, 'open');
  tournamentTotals.delete(player.p);
});

host.onInput((frame) => {
  // Default to slot 0 if the broker dropped the p field (older
  // pre-multi-aware broker / accidental single-mode session). Keeps
  // single-phone play working even if the multi broker isn't live.
  const p = typeof frame.p === 'number' ? frame.p : 0;
  // Any input frame counts as a user gesture for AudioContext init.
  if (gameControls.paused) return;
  initAudio();
  // Spectator phones don't drive the game — they can only cheer.
  if (spectatorSlots.has(p)) {
    if (frame.k === 'A' && frame.v === '1') {
      cheers.push({ slot: p, until: performance.now() + 1200, colour: PLAYER_COLOURS[p % PLAYER_COLOURS.length] ?? '#b48cff' });
      // Small shake on the host canvas so the audience feels rewarded
      // for cheering. Smaller intensity than a kill-hit (14) so it
      // reads as celebratory not violent.
      triggerShake(world, 5, 0.35);
      // Send a refreshed view so the spectator card highlights the cheer.
      for (const s of spectatorSlots) host.sendSpectatorView(s, buildSpectatorView(s));
    }
    return;
  }
  applyInput(world, p, frame.k, frame.v);
});

// Local mute toggle on M (operator at the host machine can hush the
// page mid-party). Click anywhere on the canvas also initialises the
// audio context as a fallback for first-time visitors who haven't
// scanned yet.
const gameControls = mountGameControls({
  canStart: () => world.round.kind === 'lobby' && host.players.some(player => player.connected),
  start: () => { initAudio(); autoFillAI(world); world.round = { kind: 'countdown', startedAt: world.time }; },
  restart: () => { Object.assign(world, newWorld()); for (const player of host.players) readyPlayer(player); autoFillAI(world); world.round = { kind: 'countdown', startedAt: world.time }; },
  pause: (paused) => { setAudioPaused(paused); host.broadcast(pauseMessage(paused)); for (const tank of world.tanks.values()) tank.thrust = false; },
  muted: isMuted,
  toggleSound: () => { initAudio(); toggleMuted(); },
});

window.addEventListener('keydown', (e) => {
  if (e.key === 'm' || e.key === 'M') {
    initAudio();
    toggleMuted();
  }
});
canvas.addEventListener('click', () => initAudio());

// Fixed-step loop — request frame, advance world by elapsed seconds
// (clamped so a backgrounded tab doesn't dump a 10-second step on
// resume), redraw. No autoFillAI on cold boot — the world stays in
// its lobby state, showing pairing QR cards, until a human pairs
// and presses a button.
//
// Audio is driven off observable state diffs: a new bullet means a
// shoot, a tank crossing from alive=true to alive=false means a
// kill, a player HP that just dropped (but didn't die) means a hit,
// a fresh shieldUntil means a shield activation, and a round-over
// kind toggle plays the winner / loser fanfare.
let last = performance.now();
const prevBullets = new Set<Bullet>();
const prevHp = new Map<number, number>();
const prevAlive = new Map<number, boolean>();
const prevShieldUntil = new Map<number, number>();
let prevRoundKind: 'lobby' | 'countdown' | 'playing' | 'over' = 'lobby';
let prevWinnerP: number | null = null;
function loop(t: number): void {
  const dt = Math.min(0.05, (t - last) / 1000);
  last = t;
  if (!gameControls.paused) step(world, dt);
  gameControls.update();
  // State-diff pass — drives both SFX (gated by isMuted) and haptics
  // (always fired, since phone vibration is independent of the host's
  // muted TV speakers).
  const muted = isMuted();
  for (const b of world.bullets) {
    if (!prevBullets.has(b)) {
      if (!muted) sfxShoot();
      const owner = world.tanks.get(b.ownerP);
      if (owner && !owner.ai) host.haptic('tap', b.ownerP);
    }
  }
  prevBullets.clear();
  for (const b of world.bullets) prevBullets.add(b);
  for (const tank of world.tanks.values()) {
    const pHp = prevHp.get(tank.p);
    const pAlive = prevAlive.get(tank.p);
    const pShield = prevShieldUntil.get(tank.p) ?? 0;
    if (pAlive === true && !tank.alive) {
      if (!muted) sfxKill();
      if (!tank.ai) host.haptic('fail', tank.p);
    } else if (pHp !== undefined && tank.hp < pHp && tank.alive) {
      if (!muted) sfxHit();
      if (!tank.ai) host.haptic('pulse', tank.p);
    }
    if (tank.shieldUntil > pShield && tank.shieldUntil > world.time) {
      if (!muted) sfxShield();
      if (!tank.ai) host.haptic('thud', tank.p);
    }
    prevHp.set(tank.p, tank.hp);
    prevAlive.set(tank.p, tank.alive);
    prevShieldUntil.set(tank.p, tank.shieldUntil);
  }
  // Round-end fanfare + winner haptic + tournament award.
  if (world.round.kind === 'over' && prevRoundKind !== 'over') {
    const winnerP = world.round.winnerP;
    if (winnerP !== prevWinnerP) {
      const winnerTank: Tank | undefined = winnerP !== null ? world.tanks.get(winnerP) : undefined;
      if (winnerTank && !winnerTank.ai) {
        if (!muted) sfxWin();
        host.haptic('win', winnerTank.p);
        host.awardScore(winnerTank.p, 5, roundWinLabel());
        tournamentTotals.set(winnerTank.p, (tournamentTotals.get(winnerTank.p) ?? 0) + 5);
      } else if (winnerTank && winnerTank.ai) {
        if (!muted) sfxLose();
      }
      prevWinnerP = winnerP;
    }
  } else if (world.round.kind !== 'over') {
    prevWinnerP = null;
  }
  const roundTransitioned = world.round.kind !== prevRoundKind;
  prevRoundKind = world.round.kind;
  // Low-HP heartbeat — engages while any human tank is alive at 1 HP
  // during active play; stops on transitions or hp recovery.
  const anyHumanCritical = world.round.kind === 'playing'
    && [...world.tanks.values()].some((t) => !t.ai && t.alive && t.hp <= 1);
  if (anyHumanCritical && !muted) startHeartbeat();
  else stopHeartbeat();
  // Decay cheer bursts.
  const now = performance.now();
  for (let i = cheers.length - 1; i >= 0; i--) {
    if (cheers[i]!.until <= now) cheers.splice(i, 1);
  }
  // Periodic spectator-view refresh on round transitions or every 1.5s.
  spectatorPushTimer -= dt;
  if (spectatorSlots.size > 0 && (roundTransitioned || spectatorPushTimer <= 0)) {
    spectatorPushTimer = 1.5;
    for (const s of spectatorSlots) host.sendSpectatorView(s, buildSpectatorView(s));
  }
  const frame: RenderFrame = { world, pairingUrl: host.pairingUrl, qrDataUrl, sessionId: host.sessionId, joinState, muted, tournamentTotals, playerNames, cheers };
  draw(ctx!, frame);
  requestAnimationFrame(loop);
}
let spectatorPushTimer = 0;
requestAnimationFrame(loop);

// Resize is handled inside draw() each frame (fitCanvas reads window).
window.addEventListener('resize', () => { /* draw handles it next frame */ });
